import React from "react";
import {
  RiPrinterLine,
  RiCloseLine,
  RiFileTextLine,
  RiArrowDownSLine,
  RiMailLine,
  RiMailSendLine,
} from "react-icons/ri";
import { RegisteredEmailOption } from "../types";
import { parseStatementEmails } from "../utils";

interface StatementHeaderActionsProps {
  customerName?: string;
  selectedEmail: string;
  setSelectedEmail: (val: string) => void;
  isEmailDropdownOpen: boolean;
  setIsEmailDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  customerRegisteredEmails: RegisteredEmailOption[];
  isSendingEmail: boolean;
  isEmailCooldownActive?: boolean;
  formattedCooldownTime?: string;
  emailDropdownRef: React.RefObject<HTMLDivElement>;
  emailInputRef: React.RefObject<HTMLTextAreaElement>;
  handleClearEmail: () => void;
  handleSelectEmail: (email: string) => void;
  onSendEmail: (emails: string[]) => void;
  onPrint: () => void;
  onClose: () => void;
  tr: (key: string) => string;
  isStatementError: boolean;
  isStatementEmpty: boolean;
}

export default function StatementHeaderActions({
  customerName,
  selectedEmail,
  setSelectedEmail,
  isEmailDropdownOpen,
  setIsEmailDropdownOpen,
  customerRegisteredEmails,
  isSendingEmail,
  isEmailCooldownActive = false,
  formattedCooldownTime = "",
  emailDropdownRef,
  emailInputRef,
  handleClearEmail,
  handleSelectEmail,
  onSendEmail,
  onPrint,
  onClose,
  tr,
  isStatementError,
  isStatementEmpty,
}: StatementHeaderActionsProps) {
  const [typingEmail, setTypingEmail] = React.useState("");
  const [confirmationEmails, setConfirmationEmails] = React.useState<string[] | null>(null);
  const [visibleRecipientCount, setVisibleRecipientCount] = React.useState(Number.MAX_SAFE_INTEGER);
  const [isMoreRecipientsOpen, setIsMoreRecipientsOpen] = React.useState(false);
  const visibleInputRef = React.useRef<HTMLInputElement>(null);
  const headerActionsRef = React.useRef<HTMLDivElement>(null);
  const recipientMeasurementsRef = React.useRef<HTMLDivElement>(null);

  const recipientEmails = parseStatementEmails(selectedEmail);
  const visibleRecipientEmails = recipientEmails.slice(0, visibleRecipientCount);
  const hiddenRecipientCount = recipientEmails.length - visibleRecipientEmails.length;
  const hiddenRecipientEmails = recipientEmails.slice(visibleRecipientEmails.length);
  const selectedRecipients = new Set(recipientEmails.map((email) => email.toLowerCase()));

  React.useLayoutEffect(() => {
    const headerActions = headerActionsRef.current;
    const measurements = recipientMeasurementsRef.current;
    if (!headerActions || !measurements) return;

    const updateVisibleRecipients = () => {
      const toolbar = headerActions.querySelector<HTMLElement>('[role="toolbar"]');
      const availableWidth = headerActions.clientWidth - (toolbar?.offsetWidth || 0) - 10;
      if (availableWidth <= 0) {
        setVisibleRecipientCount(recipientEmails.length);
        return;
      }

      const chipWidths = Array.from(measurements.children).map(
        (element) => (element as HTMLElement).offsetWidth + 6
      );
      const fieldChromeWidth = customerRegisteredEmails.length > 1 ? 176 : 152;
      const allRecipientsWidth = chipWidths.reduce((total, width) => total + width, 0);

      if (fieldChromeWidth + allRecipientsWidth <= availableWidth) {
        setVisibleRecipientCount(recipientEmails.length);
        return;
      }

      const moreLabelWidth = 72;
      let usedWidth = fieldChromeWidth + moreLabelWidth;
      let count = 0;
      for (const width of chipWidths) {
        if (usedWidth + width > availableWidth) break;
        usedWidth += width;
        count += 1;
      }
      setVisibleRecipientCount(count);
    };

    updateVisibleRecipients();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateVisibleRecipients);
      observer.observe(headerActions);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", updateVisibleRecipients);
    return () => window.removeEventListener("resize", updateVisibleRecipients);
  }, [selectedEmail, recipientEmails.length, customerRegisteredEmails.length]);

  React.useEffect(() => {
    if (hiddenRecipientCount === 0) setIsMoreRecipientsOpen(false);
  }, [hiddenRecipientCount]);

  React.useEffect(() => {
    if (!isMoreRecipientsOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!emailDropdownRef.current?.contains(event.target as Node)) {
        setIsMoreRecipientsOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [isMoreRecipientsOpen, emailDropdownRef]);

  const handleRemoveEmail = (emailToRemove: string) => {
    const remaining = recipientEmails.filter(
      (e) => e.toLowerCase() !== emailToRemove.trim().toLowerCase()
    );
    setSelectedEmail(remaining.join(", "));
  };

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const trimmedTyping = typingEmail.trim().replace(/[,;]+$/, "");
  const isTypingNotEmpty = trimmedTyping.length > 0;
  const isTypingValidEmail = isTypingNotEmpty && EMAIL_REGEX.test(trimmedTyping);

  // Email validation states
  const hasInvalidEmailInList = recipientEmails.some(
    (email) => !EMAIL_REGEX.test(email.trim())
  );
  const hasUncompletedTyping = isTypingNotEmpty && !isTypingValidEmail;
  const hasNoValidRecipient = recipientEmails.length === 0 && !isTypingValidEmail;

  const isSendEmailDisabled =
    isSendingEmail ||
    isEmailCooldownActive ||
    isStatementError ||
    isStatementEmpty ||
    hasInvalidEmailInList ||
    hasUncompletedTyping ||
    hasNoValidRecipient;

  const sendEmailTooltip = hasInvalidEmailInList || hasUncompletedTyping
    ? tr("Please enter a valid email address")
    : hasNoValidRecipient
    ? tr("Please enter an email address")
    : isEmailCooldownActive
    ? `${tr("Resend available in")} ${formattedCooldownTime}`
    : tr("Send Email");

  const addRecipient = (emailToAdd: string) => {
    const trimmed = emailToAdd.trim().replace(/[,;]+$/, "");
    if (!trimmed) return;
    if (!selectedRecipients.has(trimmed.toLowerCase())) {
      const next = [...recipientEmails, trimmed].join(", ");
      setSelectedEmail(next);
    }
    setTypingEmail("");
  };

  const handleSendClick = () => {
    if (isSendEmailDisabled) return;
    const emails = parseStatementEmails(
      [...recipientEmails, ...(isTypingValidEmail ? [trimmedTyping] : [])].join(", ")
    );
    if (isTypingValidEmail) {
      if (!selectedRecipients.has(trimmedTyping.toLowerCase())) {
        const next = [...recipientEmails, trimmedTyping].join(", ");
        setSelectedEmail(next);
      }
      setTypingEmail("");
    }
    if (emails.length > 1) {
      setConfirmationEmails(emails);
    } else {
      onSendEmail(emails);
    }
  };

  return (
    <div className="no-print bg-slate-900 text-white px-6 py-3.5 flex flex-wrap items-center justify-between gap-4 shrink-0">
      {confirmationEmails && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/60 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="statement-email-confirmation-title"
            className="w-full max-w-md rounded-xl bg-white p-6 text-slate-900 shadow-xl"
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.stopPropagation();
                setConfirmationEmails(null);
              }
              if (event.key === "Tab") {
                const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>("button");
                const first = buttons[0];
                const last = buttons[buttons.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                  event.preventDefault();
                  last.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                  event.preventDefault();
                  first.focus();
                }
              }
            }}
          >
            <h3 id="statement-email-confirmation-title" className="text-lg font-semibold mb-3">
              {tr("Confirm Send Email")}
            </h3>
            <p>{tr("Send this statement to all of the following email addresses?")}</p>
            <ul className="my-4 max-h-60 overflow-y-auto list-disc pl-5">
              {confirmationEmails.map((email) => <li key={email} className="break-all">{email}</li>)}
            </ul>
            <div className="flex justify-end gap-3">
              <button type="button" autoFocus className="rounded border border-slate-300 px-4 py-2" onClick={() => setConfirmationEmails(null)}>
                {tr("Cancel")}
              </button>
              <button type="button" disabled={isSendEmailDisabled} className="rounded bg-primary px-4 py-2 text-white disabled:opacity-50" onClick={() => {
                const emails = confirmationEmails;
                setConfirmationEmails(null);
                onSendEmail(emails);
              }}>
                {tr("Confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2.5 min-w-0 shrink-0">
        <RiFileTextLine className="text-2xl text-blue-400 shrink-0" />
        <div className="min-w-0">
          <h2 className="text-lg font-semibold m-0 text-white leading-snug truncate">
            {tr("Customer Statement")}
          </h2>
          <p className="text-xs text-slate-400 m-0 truncate">
            {customerName || tr("Customer Statement")}
          </p>
        </div>
      </div>
      <div
        ref={headerActionsRef}
        className="flex items-center gap-2 sm:gap-2.5 flex-1 justify-end min-w-0 sm:flex-nowrap flex-wrap"
      >
        {/* Recipient email chips & input (Assign to style) */}
        <div
          className="relative w-max min-w-[240px] max-w-full shrink"
          ref={emailDropdownRef}
        >
          <div
            className={`relative flex flex-nowrap items-center gap-1.5 min-h-[36px] w-max min-w-full max-w-full overflow-hidden pl-8 ${
              selectedEmail && customerRegisteredEmails.length > 1
                ? "pr-14"
                : selectedEmail || customerRegisteredEmails.length > 1
                ? "pr-8"
                : "pr-3"
            } py-1 bg-slate-800 text-white border ${
              hasInvalidEmailInList || hasUncompletedTyping
                ? "border-rose-500/80 focus-within:ring-rose-500 focus-within:border-rose-500"
                : "border-slate-700 focus-within:ring-blue-500 focus-within:border-blue-500"
            } rounded-lg focus-within:ring-2 shadow-inner cursor-text transition-colors`}
            onClick={() => visibleInputRef.current?.focus()}
          >
            <RiMailLine
              className={`absolute left-2.5 top-2.5 text-sm pointer-events-none ${
                hasInvalidEmailInList || hasUncompletedTyping
                  ? "text-rose-400"
                  : "text-slate-400"
              }`}
            />

            {/* Recipient Email Badges / Chips */}
            {visibleRecipientEmails.map((email) => {
              const isEmailValid = EMAIL_REGEX.test(email.trim());
              return (
                <span
                  key={email}
                  data-testid={`statement-email-chip-${email}`}
                  className={`inline-flex shrink-0 items-center gap-1.5 pl-2.5 pr-1 py-0.5 rounded-md text-xs font-medium border shadow-sm transition-all duration-150 animate-in fade-in zoom-in-95 group ${
                    isEmailValid
                      ? "bg-slate-700/90 hover:bg-slate-700 text-slate-100 border-slate-600/80"
                      : "bg-rose-950/60 hover:bg-rose-950/80 text-rose-200 border-rose-500/80 ring-1 ring-rose-500/30"
                  }`}
                >
                  <span className="whitespace-nowrap" title={email}>
                    {email}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveEmail(email);
                    }}
                    aria-label={`Remove ${email}`}
                    data-testid={`statement-email-remove-${email}`}
                    className={`rounded p-0.5 transition-colors cursor-pointer focus:outline-none flex items-center justify-center ${
                      isEmailValid
                        ? "text-slate-400 hover:text-rose-300 hover:bg-slate-600/80"
                        : "text-rose-300 hover:text-white hover:bg-rose-600/60"
                    }`}
                    title={tr("Clear email")}
                  >
                    <RiCloseLine className="text-sm" />
                  </button>
                </span>
              );
            })}

            {hiddenRecipientCount > 0 && (
              <button
                type="button"
                aria-expanded={isMoreRecipientsOpen}
                aria-controls="statement-hidden-recipients"
                aria-label={`${hiddenRecipientCount} ${tr("more")} ${tr("Recipient emails")}`}
                data-testid="statement-email-more"
                className="shrink-0 whitespace-nowrap rounded-md border border-slate-600/80 bg-slate-700/90 px-2 py-0.5 text-xs font-medium text-slate-200 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsEmailDropdownOpen(false);
                  setIsMoreRecipientsOpen((open) => !open);
                }}
              >
                +{hiddenRecipientCount} {tr("more")}
              </button>
            )}

            {/* Visible typing input for adding more addresses */}
            <input
              ref={visibleInputRef}
              data-testid="statement-email-typing-input"
              type="text"
              value={typingEmail}
              onChange={(e) => {
                const val = e.target.value;
                if (/[,;\r\n]/.test(val)) {
                  const newEmails = parseStatementEmails(val);
                  if (newEmails.length > 0) {
                    const combined = [...recipientEmails, ...newEmails];
                    const seen = new Set<string>();
                    const unique = combined.filter((em) => {
                      const lower = em.toLowerCase();
                      if (seen.has(lower)) return false;
                      seen.add(lower);
                      return true;
                    });
                    setSelectedEmail(unique.join(", "));
                    setTypingEmail("");
                    return;
                  }
                }
                setTypingEmail(val);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === "Tab") {
                  if (typingEmail.trim()) {
                    e.preventDefault();
                    addRecipient(typingEmail);
                  }
                } else if (e.key === "Backspace" && !typingEmail && recipientEmails.length > 0) {
                  const lastEmail = recipientEmails[recipientEmails.length - 1];
                  handleRemoveEmail(lastEmail);
                }
              }}
              onBlur={() => {
                if (typingEmail.trim()) {
                  addRecipient(typingEmail);
                }
              }}
              placeholder={
                recipientEmails.length === 0
                  ? tr("Recipient emails...")
                  : tr("Add email...")
              }
              size={1}
              style={{
                width: `${Math.max(
                  typingEmail.length + 1,
                  recipientEmails.length > 0 ? 10 : 18
                )}ch`,
                maxWidth: "min(36rem, 60vw)",
              }}
              className="flex-none min-w-[64px] bg-transparent text-xs text-white placeholder-slate-400 focus:outline-none border-none p-0 py-0.5 h-6 transition-[width] duration-150"
            />

            {/* Underlying textarea for test & form compatibility */}
            <textarea
              ref={emailInputRef}
              data-testid="statement-email-input"
              aria-label={"Statement recipient emails"}
              value={selectedEmail}
              onChange={(e) => {
                setSelectedEmail(e.target.value);
              }}
              className="w-full sr-only pointer-events-none"
              tabIndex={-1}
              aria-hidden="true"
            />

            {/* Clear All and Dropdown Toggle Action Buttons */}
            <div className="absolute right-1 top-1.5 flex items-center">
              {selectedEmail && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearEmail();
                    setTypingEmail("");
                  }}
                  aria-label={"Clear emails"}
                  data-testid="statement-email-clear"
                  className="text-slate-400 hover:text-white p-1 rounded focus:outline-none cursor-pointer"
                  title={tr("Clear all emails")}
                >
                  <RiCloseLine className="text-sm" />
                </button>
              )}
              {customerRegisteredEmails.length > 1 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMoreRecipientsOpen(false);
                    setIsEmailDropdownOpen((prev) => !prev);
                  }}
                  aria-label={tr("Select registered email")}
                  data-testid="statement-email-dropdown-toggle"
                  className="text-slate-400 hover:text-white p-1 rounded focus:outline-none cursor-pointer"
                >
                  <RiArrowDownSLine
                    className={`text-sm transition-transform duration-200 ${
                      isEmailDropdownOpen ? "rotate-180 text-blue-400" : ""
                    }`}
                  />
                </button>
              )}
            </div>
          </div>

          {isMoreRecipientsOpen && hiddenRecipientCount > 0 && (
            <div
              id="statement-hidden-recipients"
              role="group"
              aria-label={tr("Recipient emails")}
              className="absolute right-0 top-full mt-1.5 z-50 w-full min-w-[280px] max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 text-slate-800 shadow-xl ring-1 ring-black/5"
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  setIsMoreRecipientsOpen(false);
                  event.stopPropagation();
                }
              }}
            >
              {hiddenRecipientEmails.map((email) => (
                <div key={email} className="flex items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-xs hover:bg-slate-100">
                  <span className="min-w-0 break-all">{email}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${email}`}
                    data-testid={`statement-email-remove-${email}`}
                    title={tr("Clear email")}
                    className="shrink-0 rounded p-1 text-slate-500 hover:bg-slate-200 hover:text-rose-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={() => handleRemoveEmail(email)}
                  >
                    <RiCloseLine className="text-sm" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div
            ref={recipientMeasurementsRef}
            aria-hidden="true"
            className="fixed -left-[9999px] top-0 invisible flex items-center gap-1.5"
          >
            {recipientEmails.map((email) => (
              <span
                key={email}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md border py-0.5 pl-2.5 pr-1 text-xs font-medium"
              >
                <span className="whitespace-nowrap">{email}</span>
                <RiCloseLine className="shrink-0 text-sm" />
              </span>
            ))}
          </div>

          {/* Registered Email Dropdown Options */}
          {isEmailDropdownOpen && customerRegisteredEmails.length > 1 && (
            <div
              role="listbox"
              aria-multiselectable="true"
              aria-label={tr("Customer Registered Emails")}
              className="absolute right-0 top-full mt-1.5 z-50 w-full min-w-[280px] bg-white text-slate-800 border border-slate-200 rounded-xl shadow-xl p-1.5 space-y-0.5 animate-in fade-in zoom-in-95 duration-100 ring-1 ring-black/5 max-h-60 overflow-y-auto"
            >
              <div className="px-2.5 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                {tr("Customer Registered Emails")}
              </div>
              {customerRegisteredEmails.map((item) => {
                const isSelected = selectedRecipients.has(item.email.toLowerCase());
                return (
                  <button
                    key={item.email}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-testid={`statement-email-option-${item.email}`}
                    onClick={() => handleSelectEmail(item.email)}
                    className={`w-full flex items-center justify-between px-2.5 py-2 text-left text-xs rounded-lg transition-colors cursor-pointer ${
                      isSelected
                        ? "bg-blue-50 text-blue-700 font-semibold"
                        : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                    }`}
                  >
                    <div className="truncate pr-2">
                      <div className="font-medium truncate">{item.email}</div>
                      {item.label && (
                        <div className="text-[10px] text-slate-400 truncate mt-0.5">
                          {item.label}
                        </div>
                      )}
                    </div>
                    {item.isDefault && (
                      <span className="shrink-0 text-[10px] bg-blue-100 text-blue-700 font-medium px-1.5 py-0.5 rounded">
                        {tr("Default")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div
          role="toolbar"
          aria-label={tr("Statement Actions")}
          className="flex items-center gap-1 shrink-0"
        >

          
          {/* Print Statement Minimal Icon Button (Separated) */}
          <div className="relative group flex items-center">
            <button
              type="button"
              aria-label={tr("Print Statement")}
              data-testid="statement-print-btn"
              disabled={isStatementError || isStatementEmpty}
              onClick={onPrint}
              className={`relative flex items-center justify-center h-8 w-8 rounded-lg text-xs font-medium transition-all duration-150 shadow-sm border ${
                isStatementError || isStatementEmpty
                  ? "bg-slate-800/40 text-slate-500 border-slate-800/60 cursor-not-allowed"
                  : "bg-slate-700 hover:bg-slate-600 text-white border-slate-500/80 hover:border-slate-400 active:bg-slate-600 cursor-pointer"
              }`}
            >
              <RiPrinterLine
                className={`text-lg ${
                  isStatementError || isStatementEmpty ? "text-slate-500" : "text-white"
                }`}
              />
              {/* Respective Tooltip for Print Statement */}
              <span
                role="tooltip"
                className="pointer-events-none absolute top-full mt-2 left-1/2 -translate-x-1/2 z-50 whitespace-nowrap rounded-md bg-slate-950 px-3.5 py-2 text-sm font-medium text-slate-200 shadow-xl border border-slate-700/80 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-all duration-150 transform -translate-y-1 group-hover:translate-y-0 normal-case"
              >
                {isStatementEmpty ? tr("No invoices to print") : tr("Print Statement")}
                <span className="absolute -top-1 left-1/2 -translate-x-1/2 border-4 border-transparent border-b-slate-950 block" />
              </span>
            </button>
          </div>

          {/* Send Email Minimal Icon Button (Primary Color) */}
          <div className="relative group flex items-center">
            <button
              type="button"
              aria-label={tr("Send Email")}
              data-testid="statement-send-email-btn"
              disabled={isSendEmailDisabled}
              onClick={handleSendClick}
              className={`relative flex items-center justify-center h-8 ${
                isEmailCooldownActive ? "px-2.5" : "w-8"
              } rounded-lg text-xs font-medium transition-all duration-150 shadow-sm ${
                isEmailCooldownActive
                  ? "bg-amber-500/20 text-amber-300 border border-amber-500/30 cursor-not-allowed"
                  : isSendEmailDisabled
                  ? "bg-primary/40 text-white/50 cursor-not-allowed"
                  : "bg-primary text-white hover:bg-primary-dark active:bg-primary-dark/90 cursor-pointer"
              }`}
            >
              {isSendingEmail ? (
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              ) : (
                <RiMailSendLine className="text-lg" />
              )}
              {isEmailCooldownActive && (
                <span
                  className="text-[10px] font-mono text-amber-300 font-medium ml-1.5 whitespace-nowrap select-none"
                  data-testid="statement-email-cooldown-timer"
                >
                  {tr("Resend in")} {formattedCooldownTime}
                </span>
              )}
              {/* Respective Tooltip for Send Email */}
              <span
                role="tooltip"
                data-testid="statement-send-email-tooltip"
                style={{ maxWidth: "min(22rem, calc(100vw - 2rem))" }}
                className="pointer-events-none absolute right-0 top-full mt-2 z-50 w-max whitespace-normal break-words rounded-md bg-slate-950 px-3.5 py-2 text-center text-sm font-medium leading-snug text-slate-200 shadow-xl border border-slate-700/80 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-all duration-150 transform -translate-y-1 group-hover:translate-y-0 normal-case"
              >
                {sendEmailTooltip}
                <span className="absolute -top-1 right-3 border-4 border-transparent border-b-slate-950 block" />
              </span>
            </button>
          </div>

          {/* Close Button */}
          <div className="relative group flex items-center ml-1">
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors h-8 w-8 flex items-center justify-center rounded-lg hover:bg-slate-800 shrink-0 cursor-pointer"
              aria-label={tr("Close")}
            >
              <RiCloseLine className="text-xl" />
            </button>
            <span
              role="tooltip"
              data-testid="statement-close-tooltip"
              className="pointer-events-none absolute right-0 top-full mt-2 z-50 whitespace-nowrap rounded-md border border-slate-700/80 bg-slate-950 px-3.5 py-2 text-sm font-medium text-slate-200 shadow-xl opacity-0 transition-all duration-150 transform -translate-y-1 group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:opacity-100 normal-case"
            >
              {tr("Close")}
              <span className="absolute -top-1 right-3 block border-4 border-transparent border-b-slate-950" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
